import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, logOut } from '../store/slices/authSlice';
import { authApi, reduxUserFromVerifyPayload } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../store/store';

const PUBLIC_PATHS = ['/login', '/login2', '/forgot-password', '/reset-password', '/privacy', '/terms', '/cookies', '/dpa'];

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isVerifying = useRef(false);

  useEffect(() => {
    const verifySession = async () => {
      // Prevent multiple simultaneous verification attempts
      if (isVerifying.current) {
        return;
      }

      // If we already have valid user data, don't verify
      if (currentUser?.id) {
        return;
      }

      isVerifying.current = true;

      try {
        const response = await authApi.verifyAccess();
        
        if (response.message === 'Valid Session' && response.data) {
          const d = response.data;
          if (d.userID) {
            dispatch(setUser(reduxUserFromVerifyPayload(d)));
          } else {
            console.warn('verifyAccess returned user data without userID, not updating');
          }
        } else {
          console.error('Invalid session response:', response);
          // Only logout if we don't have valid user data already
          if (!currentUser?.id && !PUBLIC_PATHS.includes(location.pathname)) {
            dispatch(logOut());
            navigate('/login', { replace: true });
          }
        }
      } catch (error: any) {
        console.error('Session verification failed:', error);
        // Only logout if we don't have valid user data already
        if (!currentUser?.id && !PUBLIC_PATHS.includes(location.pathname)) {
          dispatch(logOut());
          navigate('/login', { replace: true });
        }
      } finally {
        isVerifying.current = false;
      }
    };

    // Only verify session if we don't have valid user data
    if (!currentUser?.id) {
      verifySession();
    }
    
    // Set up interval for periodic checks (only if we have valid user data)
    let interval: NodeJS.Timeout | null = null;
    if (currentUser?.id) {
      interval = setInterval(() => {
        // Only do periodic checks if we have valid user data
        if (currentUser?.id) {
          verifySession();
        }
      }, 5 * 60 * 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, navigate, currentUser?.id]); // Only depend on currentUser.id, not the entire currentUser object
};